/**
 * @quotech/api - API Client
 * 
 * Universal Axios client for all Quotech modules.
 * 
 * Features:
 * - Automatic auth header injection
 * - Automatic token refresh
 * - Organisation context header
 * - Centralized error handling
 * - Request/response logging (dev)
 */

import axios, { 
  type AxiosInstance, 
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import type { ApiConfig, ApiError, TokenProvider, InterceptorConfig } from './types'
import { decodeJwtSub, broadcastIdentityChange, handleResponseIdentity } from '@quotech/auth'
import { releaseFormDataContentType } from './formDataRequest'
import { getCurrentActionId, recordHttpRequest } from './observability'

/**
 * Determine API base URL at build time.
 * Always uses the full backend URL (no Vite proxy).
 *
 * WHY inline instead of importing @/config/api?
 *   @/ alias resolves to each consuming app's src/ directory. Not every app
 *   has config/api.ts, so the import would fail at build time. Determining
 *   the URL here keeps the package self-contained.
 *
 * IMPORTANT: Use getApiBase() in module-level `const` declarations to avoid
 *   Rollup TDZ crashes. When Rollup inlines modules, `const` exports can be
 *   reordered, causing "Cannot access 'X' before initialization" errors.
 *   getApiBase() is safe because function calls don't TDZ.
 */
let _apiBase: string | undefined

/**
 * Lazy-safe API base URL getter.
 * Use this instead of API_BASE in module-level const declarations
 * to avoid Rollup TDZ (Temporal Dead Zone) crashes.
 *
 * On *.quotech.ai domains (when VITE_API_URL is not set), returns ''
 * so requests go through the frontend Nginx reverse proxy (/api/ → backend).
 * This eliminates CORS. Hub builds set VITE_API_URL explicitly in CI so
 * they are unaffected by this change.
 *
 * TO REVERT: replace body with:
 *   _apiBase = import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'
 */
export function getApiBase(): string {
  if (_apiBase === undefined) {
    const envUrl = import.meta.env.VITE_API_URL
    if (envUrl) {
      _apiBase = envUrl
    } else if (typeof window !== 'undefined' && window.location.hostname.endsWith('.quotech.ai')) {
      _apiBase = ''
    } else {
      _apiBase = 'https://quotech-backend.quotech.ai'
    }
  }
  return _apiBase
}

// Backward-compatible constant — safe inside this module, but consumers
// that assign it to a module-level const may TDZ. Prefer getApiBase().
export const API_BASE: string = getApiBase()

// Default configuration (uses lazy getter to be safe)
const DEFAULT_CONFIG: ApiConfig = {
  baseURL: `${getApiBase()}/api`,
  timeout: 30000,
  withCredentials: false,
}

// Token refresh state
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback)
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token))
  refreshSubscribers = []
}

/**
 * Create an API client with interceptors
 * 
 * NOTE: autoRefresh is disabled by default to prevent race conditions with
 * SessionManager. SessionManager in @quotech/auth is the ONLY component
 * that should refresh tokens. When SessionManager refreshes, it updates
 * the Zustand store and API requests will use the new token automatically.
 */
export function createApiClient(
  config: Partial<ApiConfig> = {},
  interceptorConfig: InterceptorConfig = {}
): AxiosInstance {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config }
  const {
    tokenProvider,
    // CRITICAL: Default to false to prevent race conditions with SessionManager
    // SessionManager is the single source of truth for token refresh
    autoRefresh = false,
    refreshEndpoint = '/auth/refresh-token/',
    enableLogging = import.meta.env.MODE === 'development',
    onRefreshFailed,
  } = interceptorConfig

  const client = axios.create({
    baseURL: mergedConfig.baseURL,
    timeout: mergedConfig.timeout,
    withCredentials: mergedConfig.withCredentials,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - add auth and org headers
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // --- Doubled-/api guard (BUG-2026-07-04-HUB-DOUBLED-API-404) --------
      // This client's baseURL conventionally ends in `/api` (DEFAULT_CONFIG),
      // yet many hub call sites also write request paths starting `/api/…`.
      // Axios joins those to `…/api/api/…`, a path family the backend never
      // registers (all routes mount at `api/<app>/…`), so every such call
      // 404s. Coerce at the boundary: when THIS client's baseURL ends in
      // `/api`, strip the duplicate leading segment so both historical path
      // conventions resolve to the same real route. Absolute URLs bypass
      // baseURL in axios and are left untouched.
      const joinBase = config.baseURL ?? mergedConfig.baseURL ?? ''
      const isAbsoluteUrl = typeof config.url === 'string' && /^([a-z][a-z\d+\-.]*:)?\/\//i.test(config.url)
      if (typeof config.url === 'string' && !isAbsoluteUrl && /\/api\/?$/.test(joinBase)) {
        if (config.url.startsWith('/api/')) {
          config.url = config.url.slice('/api'.length)
        } else if (config.url.startsWith('api/')) {
          config.url = config.url.slice('api'.length)
        }
      }

      // Add auth header
      if (tokenProvider && !(config as unknown as { skipAuth?: boolean }).skipAuth) {
        const token = tokenProvider.getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      // Add org header
      if (tokenProvider && !(config as unknown as { skipOrg?: boolean }).skipOrg) {
        const orgId = tokenProvider.getOrgId()
        if (orgId) {
          config.headers['X-Organisation-ID'] = orgId
        }
      }

      // FE↔BE seam correlation (FRAME_TEMPLATE guardrail #6): stamp the
      // in-flight mutation's actionId as X-Correlation-ID on WRITE requests.
      // The backend's CorrelationIdMiddleware sanitises + adopts it, so the
      // id in window.__QT_OBS__ joins directly to the Django structured logs
      // and audit.ServerTimingSample. Reads are left alone (high volume, and
      // query taps carry their own keys); an explicitly-set header wins. The
      // header is in the backend CORS allow-list ("x-correlation-id").
      const method = (config.method ?? 'get').toLowerCase()
      if (method !== 'get' && method !== 'head' && !config.headers['X-Correlation-ID']) {
        const actionId = getCurrentActionId()
        if (actionId) config.headers['X-Correlation-ID'] = actionId
      }

      try {
        recordHttpRequest()
      } catch {
        // Observability must never break a request.
      }

      // Log request (dev only)
      if (enableLogging) {
        console.log(`[@quotech/api] ${config.method?.toUpperCase()} ${config.url}`)
      }

      releaseFormDataContentType(config)
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - identity-drift detection + error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Server-asserted identity check (BUG-2026-05-21 Option 3).
      // Every authenticated response carries `X-User-Id` (stamped by
      // IdentityResponseHeaderMiddleware on the backend). We compare
      // it to the per-tab sessionStorage pin; a mismatch means the
      // cookie jar was swapped by a sibling tab between our request
      // and this response → broadcast + reload after 150 ms.
      //
      // Cast: axios's `AxiosResponseHeaders | Partial<RawAxiosHeaders>`
      // union doesn't match our `ResponseHeaderBag` literally, but
      // every concrete shape axios produces (the legacy plain-object
      // form AND the v1 `AxiosHeaders` class instance) is duck-typed-
      // compatible with `_readHeader`'s `.get()`/record-lookup ladder.
      try {
        handleResponseIdentity(
          response.headers as unknown as Parameters<typeof handleResponseIdentity>[0],
          { reasonTag: '@quotech/api' },
        )
      } catch { /* identity stamp must never break the success path */ }

      // Log response (dev only)
      if (enableLogging) {
        console.log(`[@quotech/api] ${response.status} ${response.config.url}`)
      }
      return response
    },
    async (error: AxiosError) => {
      // Run identity-drift check on error responses too — a 403/500
      // can still carry the server's X-User-Id and we want to
      // detect drift before the error bubbles into UI state.
      if (error.response?.headers) {
        try {
          handleResponseIdentity(
            error.response.headers as unknown as Parameters<typeof handleResponseIdentity>[0],
            { reasonTag: '@quotech/api-error' },
          )
        } catch { /* never break the error path */ }
      }
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && tokenProvider && autoRefresh && !originalRequest._retry) {
        if (isRefreshing) {
          // Wait for ongoing refresh
          return new Promise((resolve) => {
            subscribeTokenRefresh((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(client(originalRequest))
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        // Capture the previous access-token `sub` BEFORE refresh so we
        // can detect a cross-tab identity swap. See
        // BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT.
        const previousSub = decodeJwtSub(tokenProvider.getAccessToken())

        try {
          const refreshToken = tokenProvider.getRefreshToken()
          if (!refreshToken) {
            throw new Error('No refresh token')
          }

          const response = await axios.post(
            `${mergedConfig.baseURL}${refreshEndpoint}`,
            { refresh: refreshToken }
          )

          const newAccessToken = response.data.access
          const newRefreshToken = response.data.refresh

          // Cross-tab identity guard. If the refresh response carries a
          // token for a different user, refuse the swap — a sibling
          // tab logged in as another user and the shared refresh
          // cookie now belongs to a different identity. We tolerate
          // previousSub == null (cold-mount first refresh).
          const newSub = decodeJwtSub(newAccessToken)
          if (previousSub && newSub && previousSub !== newSub) {
            // BUG-2026-05-22-EVERY-SECOND-RELOAD-LOOP (fifth manifestation):
            //   Mirror the cleanup that lives in axiosSetup.ts on
            //   the master SPA. Clear legacy localStorage bearer
            //   keys AND the sessionStorage tab pin so post-reload
            //   the new identity is bootstrapped cleanly. Without
            //   the sessionStorage clear, the post-reload first
            //   response trips handleResponseIdentity (also in
            //   @quotech/auth) and reloads again — same 1Hz loop
            //   that was reported on dashboard.quotech.ai.
            try {
              localStorage.removeItem('IdToken')
              localStorage.removeItem('accessTokennew')
              localStorage.removeItem('access_token')
              localStorage.removeItem('refreshToken')
            } catch { /* private mode / quota — ignore */ }
            try {
              sessionStorage.removeItem('quotech_tab_user_id')
            } catch { /* private mode / quota — ignore */ }
            console.warn(
              '[@quotech/api] Refresh returned a token for a different user — ' +
              `previousSub='${previousSub}' newSub='${newSub}'. ` +
              'Cleared stale bearer + tab pin. Refusing the swap; ' +
              'broadcasting identity change.',
            )
            try {
              broadcastIdentityChange({
                userId: newSub,
                orgId: null,
                reason: '@quotech/api.refresh-sub-mismatch',
              })
            } catch { /* ignore */ }
            if (typeof window !== 'undefined') {
              setTimeout(() => { window.location.reload() }, 150)
            }
            return Promise.reject(error)
          }

          tokenProvider.updateTokens(newAccessToken, newRefreshToken)
          onTokenRefreshed(newAccessToken)

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          }

          return client(originalRequest)
        } catch (refreshError) {
          // BUG-2026-06-04-LOGOUT-EVERY-HOUR: only a DEFINITIVE 401/403 from
          // the refresh endpoint proves the refresh credential is gone. A
          // network error, timeout, 5xx, 429 or CORS failure is TRANSIENT —
          // the 30-day refresh cookie is likely still alive, so we must NOT
          // clearAuth() / redirect. Preserve the session and reject; the
          // caller / SessionManager will retry.
          const refreshStatus = (refreshError as AxiosError)?.response?.status
          const isDefinitiveDead = refreshStatus === 401 || refreshStatus === 403
          if (isDefinitiveDead) {
            console.error('[@quotech/api] Token refresh definitively failed (401/403):', refreshError)
            tokenProvider.clearAuth()
            onRefreshFailed?.()
          } else {
            console.info(
              '[@quotech/api] Transient refresh failure (status=' +
                (refreshStatus ?? 'none') +
                ') — preserving session, NOT signing out',
            )
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // Convert to ApiError. `response` is carried through (C4-MR4) because
      // years of hub code — and setupHubApi's own 401 interceptor — read the
      // AxiosError shape (`err.response?.status`); without it every such
      // read silently resolved undefined and, e.g., the session-expired
      // redirect never fired.
      const apiError: ApiError = {
        message: getErrorMessage(error),
        status: error.response?.status || 0,
        code: (error.response?.data as Record<string, unknown>)?.code as string | undefined,
        details: error.response?.data as Record<string, unknown>,
        originalError: error,
        response: error.response,
      }

      // Log error (dev only)
      if (enableLogging) {
        console.error(`[@quotech/api] Error:`, apiError)
      }

      return Promise.reject(apiError)
    }
  )

  return client
}

/**
 * Extract the most human-readable message from ANY thrown API error —
 * AxiosError, our ApiError, a fetch Error, or a plain string. Prefers the
 * backend's structured body (detail/message/error) over transport-level
 * text like "Request failed with status code 403".
 *
 * Exists because ad-hoc `err?.response?.data?.detail || err?.message`
 * chains were re-implemented (or forgotten) per call site — the forgotten
 * ones showed users the generic axios message while the backend had sent a
 * clear explanation (BUG-2026-07-07-QDRIVE-UPLOAD-403-MODULE-GATE).
 */
export function extractApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (typeof err === 'string' && err) return err
  if (err && typeof err === 'object') {
    // AxiosError-shaped (or our ApiError which carries `response` through).
    const maybeAxios = err as { response?: { data?: unknown }; isAxiosError?: boolean }
    if (maybeAxios.response) {
      return getErrorMessage(err as AxiosError) || fallback
    }
    // Our ApiError already resolved a good message in the interceptor.
    const withMessage = err as { message?: unknown }
    if (typeof withMessage.message === 'string' && withMessage.message) {
      return withMessage.message
    }
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

/**
 * Extract error message from Axios error
 */
function getErrorMessage(error: AxiosError): string {
  const data = error.response?.data as Record<string, unknown> | undefined

  // Try common error message fields
  if (data?.message && typeof data.message === 'string') {
    return data.message
  }
  if (data?.detail && typeof data.detail === 'string') {
    return data.detail
  }
  if (data?.error && typeof data.error === 'string') {
    return data.error
  }
  if (Array.isArray(data?.errors) && data.errors[0]?.message) {
    return data.errors[0].message
  }

  // Network errors
  if (error.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.'
  }
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }

  // HTTP status messages
  const status = error.response?.status
  if (status === 401) return 'Unauthorized. Please log in again.'
  if (status === 403) return 'You do not have permission to perform this action.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 422) return 'Validation error. Please check your input.'
  if (status === 429) return 'Too many requests. Please try again later.'
  if (status && status >= 500) return 'Server error. Please try again later.'

  return error.message || 'An unexpected error occurred.'
}

/**
 * Default API client (connects to @quotech/auth automatically)
 */
let defaultClient: AxiosInstance | null = null

export function getDefaultClient(): AxiosInstance {
  if (!defaultClient) {
    // Create client without auth initially
    // Modules should call setupDefaultClient with their auth provider
    defaultClient = createApiClient()
  }
  return defaultClient
}

/**
 * Configure the shared default client.
 *
 * `interceptorConfig` (C4-MR7) lets a host opt into the reactive 401
 * refresh-and-retry path (`autoRefresh: true`) and hook definitive refresh
 * failure (`onRefreshFailed`). The master SPA uses this to replace its
 * private `src/shared/lib/api-client.ts` fork; hubs keep the default
 * (SessionManager owns proactive refresh, setupHubApi owns the 401 path).
 */
export function setupDefaultClient(
  tokenProvider: TokenProvider,
  interceptorConfig: Omit<InterceptorConfig, 'tokenProvider'> = {},
): AxiosInstance {
  defaultClient = createApiClient({}, { ...interceptorConfig, tokenProvider })
  return defaultClient
}

export default createApiClient

